import apiClient from './client';
import { getAuthSession } from '../services/auth';
import type {
  AgentHistoryResponse,
  AgentStartStopResult,
  AgentStatusInfo,
  BindPlatformModelResult,
  BootstrapResponse,
  ChatRequest,
  ChatResponse,
  ChatStreamEvent,
  CheckinResult,
  CreatePlaygroundAgentInput,
  ModelCatalogResponse,
  PlaygroundAgent,
  SyncResult,
  UploadResult,
  UsageResponse,
} from '../types';

// ------------------------------------------------------------------
// Playground 端点封装（鉴权 = 用户 JWT，经 Axios 拦截器自动携带）
//   GET  /playground/bootstrap                 → 工作台首屏聚合数据
//   POST /playground/agents                    → 新建 Agent
//   GET  /playground/agents                    → Agent 列表
//   POST /playground/agents/{id}/start|stop    → 启停运行时
//   GET  /playground/agents/{id}/status        → 运行状态
//   POST /playground/agents/{id}/chat          → OpenAI 兼容对话（stream=true 时 SSE）
//   GET  /playground/agents/{id}/history       → 会话历史（升序，≤200 条）
//   POST /playground/agents/{id}/sync|unsync   → 同步/取消同步到应龙坊市（D「我的 Agent」可见性）
//   GET  /playground/usage                     → 用量汇总
//   GET  /playground/model-catalog             → 平台模型目录（按模态分组）
//   POST /playground/agents/{id}/model/platform → 绑定平台模型（422 条目不可用 / 503 未配置 key）
//   POST /playground/checkin                   → 每日签到领取 token 与时长额度
//   POST /playground/agents/{id}/uploads       → 对话附件上传（图片 ≤5MB / 文本 ≤1MB）
// ------------------------------------------------------------------

export async function fetchBootstrap(): Promise<BootstrapResponse> {
  const res = await apiClient.get<BootstrapResponse>('/playground/bootstrap');
  return res.data;
}

export async function createPlaygroundAgent(input: CreatePlaygroundAgentInput): Promise<PlaygroundAgent> {
  const res = await apiClient.post<PlaygroundAgent>('/playground/agents', input);
  return res.data;
}

export async function fetchPlaygroundAgents(): Promise<PlaygroundAgent[]> {
  const res = await apiClient.get<PlaygroundAgent[]>('/playground/agents');
  return res.data;
}

export async function startAgent(agentId: string): Promise<AgentStartStopResult> {
  const res = await apiClient.post<AgentStartStopResult>(`/playground/agents/${agentId}/start`);
  return res.data;
}

export async function stopAgent(agentId: string): Promise<AgentStartStopResult> {
  const res = await apiClient.post<AgentStartStopResult>(`/playground/agents/${agentId}/stop`);
  return res.data;
}

export async function fetchAgentStatus(agentId: string): Promise<AgentStatusInfo> {
  const res = await apiClient.get<AgentStatusInfo>(`/playground/agents/${agentId}/status`);
  return res.data;
}

/** 非流式对话：返回 OpenAI 兼容响应体 */
export async function chatWithAgent(agentId: string, request: ChatRequest): Promise<ChatResponse> {
  const res = await apiClient.post<ChatResponse>(`/playground/agents/${agentId}/chat`, request);
  return res.data;
}

/**
 * 流式对话（SSE）：POST stream=true，逐 chunk 产出 OpenAI 兼容事件。
 * fetch 不走 Axios 拦截器，故在此手动携带认证头。
 * @param signal 可选 AbortController.signal：停止生成时中断读取（中断视为正常结束）
 */
export async function* streamChat(
  agentId: string,
  request: ChatRequest,
  signal?: AbortSignal
): AsyncGenerator<ChatStreamEvent> {
  const session = getAuthSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.accessToken) headers['Authorization'] = `Bearer ${session.accessToken}`;
  if (session?.tenantId) headers['X-Tenant-Id'] = session.tenantId;

  const res = await fetch(`/api/v1/playground/agents/${agentId}/chat`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ ...request, stream: true }),
    signal,
  });
  if (!res.ok || !res.body) {
    // 尽力提取后端中文 detail（预算 429 / LLM 未配置 422 / 上游 503 等）
    let detail = '';
    try {
      const body = (await res.json()) as { detail?: unknown };
      if (typeof body.detail === 'string') detail = body.detail;
      else if (Array.isArray(body.detail)) {
        detail = body.detail
          .map((d) => (d && typeof d === 'object' && 'msg' in d ? String((d as { msg: unknown }).msg) : ''))
          .filter(Boolean)
          .join('；');
      }
    } catch {
      // 非 JSON 响应体，忽略
    }
    throw new ChatStreamError(res.status, detail);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (signal?.aborted) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith('data:')) continue;
      const data = trimmed.slice(5).trim();
      if (data === '[DONE]') return;
      try {
        yield JSON.parse(data) as ChatStreamEvent;
      } catch {
        // 跳过无法解析的数据行，保持流不中断
      }
    }
  }
}

export async function fetchUsage(): Promise<UsageResponse> {
  const res = await apiClient.get<UsageResponse>('/playground/usage');
  return res.data;
}

/** 会话历史（升序，≤200 条，只有 user/assistant；无历史返回空数组） */
export async function fetchAgentHistory(agentId: string): Promise<AgentHistoryResponse> {
  const res = await apiClient.get<AgentHistoryResponse>(`/playground/agents/${agentId}/history`);
  return res.data;
}

/**
 * 同步到应龙坊市（D 侧）：该 Agent 出现在应龙「我的 Agent」（未发布区），
 * 上架/下架市场由应龙坊市全权管理。幂等；纯本地标记，不调远端。
 */
export async function syncAgent(agentId: string): Promise<SyncResult> {
  const res = await apiClient.post<SyncResult>(`/playground/agents/${agentId}/sync`);
  return res.data;
}

/**
 * 取消同步：该 Agent 从应龙「我的 Agent」消失。幂等（already=true）；
 * 已在应龙上架的应用不受影响（下架请在应龙坊市操作，detail 会提示）。
 */
export async function unsyncAgent(agentId: string): Promise<SyncResult> {
  const res = await apiClient.post<SyncResult>(`/playground/agents/${agentId}/unsync`);
  return res.data;
}

/**
 * 删除 Agent（释放练习名额）。走后端 DELETE /agents/{id}：先停并清理运行时，
 * 再级联清理该 Agent 的会话、消息与知识条目，最后删 Agent；不可恢复。
 */
export async function deleteAgent(agentId: string): Promise<void> {
  await apiClient.delete(`/agents/${agentId}`);
}

/** 平台模型目录：按 文本/视觉/图像/视频 分组；available=false 的条目展示但禁用 */
export async function fetchModelCatalog(): Promise<ModelCatalogResponse> {
  const res = await apiClient.get<ModelCatalogResponse>('/playground/model-catalog');
  return res.data;
}

/**
 * 绑定平台推荐模型（按目录 key）。
 * 422 = 条目不可用（coming_soon）；503 = 平台未配置 key —— detail 均为中文，UI 直接 toast。
 */
export async function bindPlatformModel(agentId: string, catalogKey: string): Promise<BindPlatformModelResult> {
  const res = await apiClient.post<BindPlatformModelResult>(`/playground/agents/${agentId}/model/platform`, {
    catalog_key: catalogKey,
  });
  return res.data;
}

/** 每日签到：领取今日 token 与时长额度（幂等，already=true 表示已签过） */
export async function checkin(): Promise<CheckinResult> {
  const res = await apiClient.post<CheckinResult>('/playground/checkin');
  return res.data;
}

/**
 * 上传对话附件（multipart 字段名 file）。
 * 图片（png/jpg/jpeg/webp/gif ≤5MB）→ kind=image；文本（txt/md ≤1MB）→ kind=text。
 * 415 类型不支持 / 413 超限，detail 为中文提示。
 */
export async function uploadAttachment(agentId: string, file: File): Promise<UploadResult> {
  const form = new FormData();
  form.append('file', file);
  const res = await apiClient.post<UploadResult>(`/playground/agents/${agentId}/uploads`, form);
  return res.data;
}

/**
 * 对话流结构化错误：携带 HTTP 状态码与后端中文 detail（budget 429 / 422 / 503 等），
 * UI 层据此给出友好提示与重试策略。
 */
export class ChatStreamError extends Error {
  status: number;
  detail: string;

  constructor(status: number, detail: string) {
    super(detail || `对话请求失败（HTTP ${status}）`);
    this.name = 'ChatStreamError';
    this.status = status;
    this.detail = detail;
  }
}
